import { Grid, InputLabel, TextField, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import GlobalModal from "../../../components/modalBox";
import Form from "../../../components/forms/Forms";
import * as yup from "yup";
import styles from "./style.module.css";
import AdminApi from "../../../config/apis/admin";
import { api, resolveUrl } from "../../../config/apis";
import { useParams, useLocation } from "react-router-dom";
import useApiData from "../../../hooks/useApiData/useApiData";

export const AddFriend = (props) => {
  const params = useParams();
  const location = useLocation();
  const { options: partnerOptions } = useApiData(
    AdminApi.FRIENDS_OF_CG_PARTNERS
  );
  const { options: placementOptions } = useApiData(
    AdminApi.FRIENDS_OF_CG_PLACEMENTS
  );

  const defaultFormData = {
    partner_type_id: "",
    partner_placement_id: "",
    organization: "",
    website_url: "",
    contact_person: "",
    email: "",
    last_interaction: "",
    status: "",
    comment: "",
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});

  const handleCloseModal = () => {
    props.setModalOpen(false);
    setErrors({});
    setFormData(defaultFormData); // Always reset form data when closing modal
  };

  const schema = yup.object().shape({
    partner_type_id: yup
      .mixed()
      .transform((value) => (value === "" ? undefined : Number(value)))
      .required("Partner type is required"),
    partner_placement_id: yup
      .mixed()
      .transform((value) => (value === "" ? undefined : Number(value)))
      .required("Placement is required"),
    organization: yup.string().required("Organization is required"),
    // website_url: yup.string().required("Website URL is required"),
    contact_person: yup.string().required("Contact person is required"),
    // email: yup
    //   .string()
    //   .email("Invalid email format")
    //   .required("Email is required"),
    last_interaction: yup.string().required("Last interaction is required"),
    status: yup
      .mixed()
      .transform((value) => (value === "" ? undefined : Number(value)))
      .required("Status is required"),
  });

  const customValidation = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const newErrors = {};

      error.inner.forEach((validationError) => {
        newErrors[validationError.path] = validationError.message;
      });

      setErrors(newErrors);
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: undefined,
    }));
  };

  // Reset form data when formSuccess changes and is true
  useEffect(() => {
    if (props.formSuccess) {
      setFormData(defaultFormData);
    }
  }, [props.formSuccess]);

  // Load data when editing
  useEffect(() => {
    // If modal is not open, reset form
    if (!props.modalOpen) {
      setFormData(defaultFormData);
      return;
    }

    // If modal is open and we have an editId, fetch the data
    if (props.editId) {
      const fetchData = async () => {
        try {
          const res = await api.get(
            resolveUrl(AdminApi.FRIENDS_OF_CG_DETAILS, props.editId)
          );
          const {
            partner_type_id,
            partner_placement_id,
            organization,
            website_url,
            contact_person,
            email,
            last_interaction,
            status,
            comment,
          } = res.data.data;

          setFormData({
            partner_type_id: partner_type_id ?? "",
            partner_placement_id: partner_placement_id ?? "",
            organization: organization ?? "",
            website_url: website_url ?? "",
            contact_person: contact_person ?? "",
            email: email ?? "",
            last_interaction: last_interaction ?? "",
            status: status ?? 1,
            comment: comment ?? "",
          });
        } catch (error) {
          console.error("Failed to fetch friend details:", error);
        }
      };
      fetchData();
    } else {
      // If adding new, reset to default
      setFormData(defaultFormData);
    }
  }, [props.modalOpen, props.editId]);

  // Map status value to display text
  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
  ];

  return (
    <>
      <GlobalModal
        open={props.modalOpen}
        onClose={handleCloseModal}
        width={550}
      >
        <Form
          successPath={location}
          submitUrl={
            props.editId
              ? AdminApi.FRIENDS_OF_CG_EDIT
              : AdminApi.FRIENDS_OF_CG_ADD
          }
          formData={formData}
          setFormSuccess={props.setFormSuccess}
          editId={props.editId}
          validate={customValidation}
          customClass={styles.tractionForm}
          transformData={(data) => {
            // After successful submission, this will be called
            // We'll add a callback to reset the form
            setTimeout(() => {
              setFormData(defaultFormData);
            }, 100);
            
            return {
              ...data,
              status: data.status === 1 ? "Active" : "Inactive",
            };
          }}
        >
          <Grid xs={12} container marginTop={4} className={styles.addFriend}>
            <Grid xs={12} item className={styles.formRow}>
              <InputLabel sx={{ textAlign: "left", marginBottom: 1 }}>
                Type *
              </InputLabel>
              <TextField
                select
                fullWidth
                placeholder="Select partner type"
                value={formData.partner_type_id}
                name="partner_type_id"
                variant="outlined"
                onChange={handleInputChange}
                error={errors.partner_type_id !== undefined}
                helperText={errors.partner_type_id}
              >
                {partnerOptions &&
                  partnerOptions.map((partner) => (
                    <MenuItem key={partner.id} value={partner.id}>
                      {partner.title}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid xs={12} item className={styles.formRow}>
              <InputLabel
                sx={{ textAlign: "left", marginBottom: 1, marginTop: 2 }}
              >
                Category *
              </InputLabel>
              <TextField
                select
                fullWidth
                placeholder="Select placement"
                value={formData.partner_placement_id}
                name="partner_placement_id"
                variant="outlined"
                onChange={handleInputChange}
                error={errors.partner_placement_id !== undefined}
                helperText={errors.partner_placement_id}
              >
                {placementOptions &&
                  placementOptions.map((placement) => (
                    <MenuItem key={placement.id} value={placement.id}>
                      {placement.title}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid xs={12} item className={styles.formRow}>
              <InputLabel
                sx={{ textAlign: "left", marginBottom: 1, marginTop: 2 }}
              >
                Organization *
              </InputLabel>
              <TextField
                onChange={handleInputChange}
                name="organization"
                variant="outlined"
                value={formData.organization}
                fullWidth
                error={errors.organization !== undefined}
                helperText={errors.organization}
              />
            </Grid>
            <Grid xs={12} item className={styles.formRow}>
              <InputLabel
                sx={{ textAlign: "left", marginBottom: 1, marginTop: 2 }}
              >
                Website
              </InputLabel>
              <TextField
                onChange={handleInputChange}
                name="website_url"
                variant="outlined"
                value={formData.website_url}
                placeholder="www.example.com"
                fullWidth
                // error={errors.website_url !== undefined}
                // helperText={errors.website_url}
              />
            </Grid>
            <Grid xs={12} item className={styles.formRow}>
              <InputLabel
                sx={{ textAlign: "left", marginBottom: 1, marginTop: 2 }}
              >
                Email
              </InputLabel>
              <TextField
                onChange={handleInputChange}
                name="email"
                variant="outlined"
                value={formData.email}
                placeholder="peter.parker@yoursite.com"
                fullWidth
                // error={errors.email !== undefined}
                // helperText={errors.email}
              />
            </Grid>
            <Grid xs={12} item className={styles.formRow}>
              <InputLabel
                sx={{ textAlign: "left", marginBottom: 1, marginTop: 2 }}
              >
                Contact Person *
              </InputLabel>
              <TextField
                onChange={handleInputChange}
                name="contact_person"
                variant="outlined"
                value={formData.contact_person}
                fullWidth
                error={errors.contact_person !== undefined}
                helperText={errors.contact_person}
              />
            </Grid>
            <Grid xs={12} item className={styles.formRow}>
              <InputLabel
                sx={{ textAlign: "left", marginBottom: 1, marginTop: 2 }}
              >
                Last Interaction *
              </InputLabel>
              <TextField
                onChange={handleInputChange}
                name="last_interaction"
                variant="outlined"
                value={formData.last_interaction}
                placeholder="Outreach for TechFest"
                fullWidth
                error={errors.last_interaction !== undefined}
                helperText={errors.last_interaction}
              />
            </Grid>
            <Grid xs={12} item className={styles.formRow}>
              <InputLabel
                sx={{ textAlign: "left", marginBottom: 1, marginTop: 2 }}
              >
                Status *
              </InputLabel>
              <TextField
                select
                fullWidth
                placeholder="Select status"
                value={formData.status}
                name="status"
                variant="outlined"
                onChange={handleInputChange}
                error={errors.status !== undefined}
                helperText={errors.status}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid xs={12} marginBottom={2} item className={styles.formRow}>
              <InputLabel
                sx={{ textAlign: "left", marginBottom: 1, marginTop: 2 }}
              >
                Comments
              </InputLabel>
              <TextField
                onChange={handleInputChange}
                name="comment"
                variant="outlined"
                value={formData.comment}
                fullWidth
                error={errors.comment !== undefined}
                helperText={errors.comment}
              />
            </Grid>
          </Grid>
        </Form>
      </GlobalModal>
    </>
  );
};